import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, pairwise } from 'rxjs/operators';
import {
    ISplitTimePartContainer, MeridiemOptions, SelectList, SplitTimeInputPossibility, SplitTimeInputUpdateOptions, TimeParts
} from '../../src/typings/models';
import { ThemeService } from '../theme/ThemeService';

/**
 * A component to accept a single value & unit of time, as well as a format,
 * that will split it into multiple inputs & join it back together in the end
 * For example, a value of 1440 minutes with a format of HH:mm should render an input for
 * 'Hours' and an input for 'Minutes', initialized to 24 hours.
 */
// If bug is found, it needs to be fixed in legacy component as well probably (apart from styling)

@Component({ 
    selector: 'cl-split-time-input',
    templateUrl: './split-time-input.component.html',
    styleUrls: ['./split-time-input.component.scss']
})
export class SplitTimeInputComponent implements OnInit {
    public static $name: string = 'splitTimeInput';

    constructor(
        private themeService: ThemeService
    ) {
    }

    @Input() public formGroup: FormGroup;

    /** If this is passed in as true, you should be able to see the values but not edit them */
    @Input() public isDisabled: boolean = false;

    /** The raw max, in the passed-in unit */
    @Input() public maxValue: number;

    /** The raw min, in the passed-in unit */
    @Input() public minValue: number;

    /** The raw value, in the passed-in unit */
    @Input() public value: number;

    /** A moment string, e.g. 'hh:mm' or 'HH:mm:ss aa' */
    @Input() public format: string;

    /** A string representing the base unit that this.value represents, e.g. 'seconds' or 'minutes' */
    @Input() public unit: moment.DurationInputArg2 = TimeParts.seconds;

    /** An outside function for updating the value in that context -- to keep component encapsulation in line with angular's roadmap */
    @Output() public onUpdate: EventEmitter<SplitTimeInputUpdateOptions> = new EventEmitter();

    /** Output for name refererence to the dynamically create form group */
    @Output() public onFormCreate: EventEmitter<string> = new EventEmitter();

    private innerFormGroup: FormGroup;

    /** Must change with formName */
    private splitTimeInputForm: angular.IFormController;

    /** A user-friendly display of the min */
    private formattedMin: string;

    /** A user-friendly display of the max */
    private formattedMax: string;

    /** We'll need to handle time formats with meridiems quite differently than normal. Let's make it easier to keep up with. */
    private hasMeridiem: boolean;

    /**
     * It's (sort of) OK if the format is invalid -- we'll just fall back to a single input
     * -- but this variable will hold the truth once it has been parsed
     */
    public isFormatValid: boolean = true;

    /** We'll pass this into the invalid-if directive to validate all of the inputs based on this.value */
    public isValid: boolean = true;

    /** Stores the possibilities that are actually being used, based on the format that was passed in */
    private timeParts: string[] = [];

    /** Stores the non-merediem timeParts for the *ngFor */
    public filteredTimeParts: string[] = [];

    public fallbackFormControl: FormControl;
    public timePartFormControls: { [name: string]: FormControl } = {};
    public meridiemFormControl: FormControl;

    public textAlertClass: string;

    /**
     * An object containing the information necessary for figuring out what to display &
     * how to put it back together when it changes -- if you need to support days or weeks, start here
     * We should probably be using TimeParts as the keys here, but it seems TypeScript doesn't support them at the moment.
     */
    public possibilities: { [name: string]: SplitTimeInputPossibility } = {
        hours: {
            getMax: () => {
                if (this.hasMeridiem) {
                    // tslint:disable-next-line: no-magic-numbers
                    return 12;
                } else if (this.maxValue) {
                    return Math.floor(moment.duration(this.maxValue, this.unit).asHours());
                } else {
                    return null;
                }
            },
            getMin: () => {
                return this.hasMeridiem ? 1 : 0;
            },
            getValue: (inputValue: number, multiplier: number) => {
                /** With meridiems active, we must treat 12 hours as zero hours to get accurate output */
                // tslint:disable-next-line: no-magic-numbers
                if (this.hasMeridiem && inputValue === 12) {
                    inputValue = 0;
                }

                return this.getValue(inputValue, multiplier);
            },
            label: 'hour',
            regexp: /[Hh]+/,
            secondsPer: 3600,
            validation: {
                fieldName: 'Hours',
            }
        },
        minutes: {
            getMax: () => {
                if (_.includes(this.timeParts, TimeParts.hours)) {
                    // tslint:disable-next-line: no-magic-numbers
                    return 59;
                } else if (this.maxValue) {
                    return Math.floor(moment.duration(this.maxValue, this.unit).asMinutes());
                } else {
                    return null;
                }
            },
            getMin: () => {
                if (this.minValue && this.format === 'mm') {
                    return Math.floor(moment.duration(this.minValue, this.unit).asMinutes());
                } else {
                    return 0;
                }
            },
            getValue: this.getValue,
            label: 'min',
            regexp: /m+/,
            secondsPer: 60,
            validation: {
                fieldName: 'Minutes',
            }
        },
        seconds: {
            getMax: () => {
                if (_.includes(this.timeParts, TimeParts.minutes)) {
                    // tslint:disable-next-line: no-magic-numbers
                    return 59;
                } else if (this.maxValue) {
                    return Math.floor(moment.duration(this.maxValue, this.unit).asSeconds());
                } else {
                    return null;
                }
            },
            getMin: () => {
                if (this.minValue && this.format === 'ss') {
                    return Math.floor(moment.duration(this.minValue, this.unit).asSeconds());
                } else {
                    return 0;
                }
            },
            getValue: this.getValue,
            label: 'sec',
            regexp: /s+/,
            secondsPer: 1,
            validation: {
                fieldName: 'Seconds',
            }
        },
        // tslint:disable-next-line: object-literal-sort-keys
        meridiem: {
            getMax: () => {
                return 1;
            },
            getMin: () => {
                return 0;
            },
            getValue: this.getValue,
            label: '',
            regexp: /[Aa]+/,
            requires: [TimeParts.hours],
            // tslint:disable-next-line: no-magic-numbers
            secondsPer: 3600 * 12,
            validation: {
                fieldName: 'Meridiem',
                min: '{{ splitTimeInputCtrl.possibilities.meridiem.min }}'
            }
        }
    };

    /**
     * This will be used with the original unit if format validation fails
     * It must be assigned upon initialization, because 'unit' will not be overwritten at the top-level by
     * an external binding at the time an inline assignment would execute -- it will still be the default 'seconds'
     */
    public fallback: SplitTimeInputPossibility;

    /** The value will be used as a multiplier to help determine the correct output time */
    public meridiemOptions: SelectList<number> = [
        { text: MeridiemOptions.am, value: 0 },
        { text: MeridiemOptions.pm, value: 1 }
    ];

    /**
     * Holds the timeParts that we will actually see on screen -- hours, minutes, seconds, etc.
     * Kept in its own object so that we can have ones for min/max too to be formatted as validation messages
     */
    private timePartContainer: ISplitTimePartContainer = {};

    public ngOnInit() {
        this.innerFormGroup = new FormGroup({});
        const formControlName = 'splitTimeInput-' + _.uniqueId();
        this.onFormCreate.emit(formControlName);
        this.formGroup.addControl(formControlName, this.innerFormGroup);
        this.textAlertClass = this.themeService.getTextAccentClass();
        this.assignFallback();
        this.determineParts();
        this.determineExtremes();
        this.populateParts(this.timePartContainer, this.value, this.timeParts);
        this.setupTimePartFormControls();
        this.filteredTimeParts = this.timeParts.filter(part => part !== TimeParts.meridiem);
        this.tryAddFallbackControl();
    }

    public getTimePartClass(last: boolean, i: number) {
        var colon = !last && this.filteredTimeParts[i + 1] !== TimeParts.meridiem;
        return colon;
    }

    /**
     * Determine whether or not the overall form should be considered valid
     */
    public isFormValid(value: number) {
        let isMaxValid = !this.maxValue || value <= this.maxValue;
        let isMinValid = !this.minValue || value >= this.minValue;

        return isMaxValid && isMinValid;
    }

    /**
     * Custom way of handling meridiem, because default material select looks ugly
     */
     updateMeridiem(meridiemOptionValue: number){
        this.meridiemFormControl.setValue(meridiemOptionValue);
        this.reconstructTime();
    }

    private isValidValidatorFactory() {
        return (control: FormControl) => {
            return !this.isValid ? { allInvalid: true } : null;
        };
    }

    /**
     * Make sure we populate hours correctly if we have a meridiem
     */
    private adjustForMeridiem(currentPart: string, computedValue: number) {
        if (currentPart === TimeParts.hours && this.hasMeridiem) {
            // tslint:disable-next-line: no-magic-numbers
            let modValue = computedValue % 12;
            // tslint:disable-next-line: no-magic-numbers
            return modValue || 12;
        } else {
            return computedValue;
        }
    }

    /**
     * Set up the fallback AFTER the external bindings have been assigned
     */
    private assignFallback() {
        this.fallback = {
            getMax: () => {
                return this.maxValue || null;
            },
            getMin: () => {
                return this.minValue || 0;
            },
            getValue: () => this.value,
            label: this.possibilities[this.unit].label,
            regexp: null,
            secondsPer: null,
        };
    }

    /**
     * Regardless of what format we need the final result to be,
     * we'll want to convert what is currently represented in the inputs to seconds first.
     */
    private convertCurrentTimeToSeconds() {
        let totalSeconds = 0;

        let len = this.timeParts.length;

        for (let i = 0; i < len; i++) {
            let currentPart = this.timeParts[i];
            let currentPossibility = this.possibilities[currentPart];

            if (this.timePartContainer.hasOwnProperty(currentPart)) {
                totalSeconds += currentPossibility.getValue(this.timePartFormControls[currentPart].value, currentPossibility.secondsPer);
            }
        }

        return totalSeconds;
    }

    /**
     * Once we have everything parsed out, we'll need to set the min/max values afterwards, to make sure all information is available
     */
    private determineExtremes() {
        if (!this.timeParts.length) {
            this.determineFallbackExtremes();
        } else {
            this.determineTimePartExtremes();
            this.determineOverallExtremes();
        }
    }

    /**
     * Figure out the real min/max value for the fallback, now that we have all of the relevant information.
     */
    private determineFallbackExtremes() {
        this.fallback.max = this.fallback.getMax();
        this.fallback.min = this.fallback.getMin();
    }

    /**
     * Parse out the correct format for displaying overarching validation
     */
    private determineOverallExtremes() {
        let minContainer: ISplitTimePartContainer = {};
        let maxContainer: ISplitTimePartContainer = {};

        this.populateParts(minContainer, this.minValue, this.timeParts);
        this.populateParts(maxContainer, this.maxValue, this.timeParts);

        this.formattedMin = this.joinTimeParts(minContainer);
        this.formattedMax = this.joinTimeParts(maxContainer);
    }

    /**
     * Figure out the real min/max value for each timePart, now that we have all of the relevant information.
     */
    private determineTimePartExtremes() {
        for (var timePart in this.timeParts) {
            let currentPart = this.timeParts[timePart];
            let currentPossibility = this.possibilities[currentPart];

            currentPossibility.max = currentPossibility.getMax();
            currentPossibility.min = currentPossibility.getMin();
        }
    }

    /**
     * Given the format, we'll need to determine which inputs to generate
     */
    private determineParts() {
        for (var possibility in this.possibilities) {
            if (this.possibilities.hasOwnProperty(possibility)) {
                let currentPossibility = this.possibilities[possibility];
                let regexp = currentPossibility.regexp;

                if (regexp.exec(this.format)) {
                    this.timeParts.push(possibility);

                    if (possibility === TimeParts.meridiem) {
                        this.hasMeridiem = true;
                    }
                }
            }
        }

        this.validateParts();
    }

    /**
     * Format the minValue/maxValue as a string to display as a validation message
     */
    private joinTimeParts(container: ISplitTimePartContainer) {
        // tslint:disable-next-line: no-magic-numbers
        let pad = (value: number) => value ? _.padStart(value.toString(), 2, '0') : '00';

        let meridiemOption = this.meridiemOptions[container.meridiem];

        let len = this.timeParts.length;

        let result: string;
        let timePartResult: string[] = []; // to easily join with colons

        for (let i = 0; i < len; i++) {
            let currentTimePart = this.timeParts[i];

            if (currentTimePart !== 'meridiem') {
                timePartResult.push(pad(container[currentTimePart]));
            }
        }

        result = timePartResult.join(':');

        if (meridiemOption) {
            result += ` ${meridiemOption.text}`;
        }

        return result;
    }

    /**
     * Determine whether to prepopulate the meridiem dropdown with AM or PM
     */
    private getInitialMeridiemValue(value: number) {
        let valueAsSeconds = moment.duration(value, this.unit).asSeconds();
        let text = valueAsSeconds >= this.possibilities[TimeParts.meridiem].secondsPer ? MeridiemOptions.pm : MeridiemOptions.am;

        let option = this.meridiemOptions.filter((option) => option.text === text)[0];

        return option.value;
    }

    /**
     * Generic getValue to use with this.possibilities for the inputs that don't need special logic
     */
    private getValue(inputValue: number, multiplier: number) {
        return inputValue * multiplier;
    }

    /**
     * Using the passed-in value, do the math & fill in the models for each part (hours, minutes, etc)
     * @param container a reference to an object that stores timeParts.
     *  this could be the model used for the value, or a separate one for determining formatted validation messages
     * @param timeParts the timeParts that we actually parsed out of the format
     */
    private populateParts(container: ISplitTimePartContainer, value: number, timeParts: string[]) {
        let remaining = moment.duration(value, this.unit).asSeconds();

        let currentPart: string;

        for (var part in timeParts) {
            currentPart = timeParts[part];

            if (currentPart !== TimeParts.meridiem) {
                if (remaining) {
                    let possibility = this.possibilities[currentPart];

                    container[currentPart] = this.adjustForMeridiem(currentPart, Math.floor(remaining / possibility.secondsPer));
                    remaining = remaining % possibility.secondsPer;
                } else {
                    container[currentPart] = this.adjustForMeridiem(currentPart, 0);
                }
            } else {
                container[currentPart] = this.getInitialMeridiemValue(value);
            }
        }
    }

    /**
     * We'll also need to convert the inputs back to the original unit
     */
    private reconstructTime() {
        let totalSeconds = this.convertCurrentTimeToSeconds();

        let newValue = moment.duration(totalSeconds, TimeParts.seconds).as(this.unit as moment.unitOfTime.Base);

        this.updateValue(newValue);
    }

    private setupTimePartFormControls() {
        const nonMeridiemParts = this.timeParts.filter(tp => tp !== TimeParts.meridiem);
        for (let part of nonMeridiemParts) {
            let validators = [
                Validators.min(this.possibilities[part].getMin()),
                Validators.max(this.possibilities[part].getMax()),
                this.isValidValidatorFactory()
            ];

            this.timePartFormControls[part] = new FormControl(this.timePartContainer[part], validators);
            // tslint:disable-next-line: no-magic-numbers
            this.timePartFormControls[part].valueChanges.pipe(debounceTime(300), distinctUntilChanged(), pairwise())
                .subscribe(([prev, next]) => {
                    this.reconstructTime();
                });
            this.innerFormGroup.addControl(this.possibilities[part].validation.fieldName, this.timePartFormControls[part]);
            setTimeout(() => {
                this.timePartFormControls[part].markAsTouched();
            }, 0);
        }

        if (this.hasMeridiem) {
            let meridiemValidators = [
                Validators.min(this.possibilities[TimeParts.meridiem].getMin()),
                Validators.max(this.possibilities[TimeParts.meridiem].getMax()),
                this.isValidValidatorFactory()
            ];

            this.meridiemFormControl = new FormControl(this.timePartContainer[TimeParts.meridiem], meridiemValidators);
            this.timePartFormControls[TimeParts.meridiem] = this.meridiemFormControl;
            this.innerFormGroup.addControl(this.possibilities[TimeParts.meridiem].validation.fieldName, this.meridiemFormControl);
            setTimeout(() => {
                this.meridiemFormControl.markAsTouched();
            }, 0);
        }
    }

    private tryAddFallbackControl() {
        if (!this.isFormatValid) {
            let validators = [
                Validators.max(this.fallback.getMax()),
                Validators.min(this.fallback.getMin()),
                this.isValidValidatorFactory()
            ];

            this.fallbackFormControl = new FormControl(this.value, validators);
            // tslint:disable-next-line: no-magic-numbers
            this.fallbackFormControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), pairwise())
                .subscribe(([prev, next]) => {
                    this.updateValue(this.fallbackFormControl.value);
                });
            this.innerFormGroup.addControl(this.unit, this.fallbackFormControl);
            setTimeout(() => {
                this.fallbackFormControl.markAsTouched();
            }, 0);
        }
    }

    /**
     * Attempt to commit the current value, if it is valid
     */
    private updateValue(newValue: number) {
        this.onUpdate.emit({ newValue: newValue });

        for (let key in this.timePartFormControls) {
            this.timePartFormControls[key].updateValueAndValidity();
        }
    }

    /**
     * Ensure that the timeParts we end up with make sense
     */
    private validateParts() {
        if (!this.timeParts.length) {
            // No format was passed in
            this.isFormatValid = false;
        } else {
            for (var part in this.timeParts) {
                let possibility = this.possibilities[this.timeParts[part]];

                if (possibility.requires) {
                    this.isFormatValid = _.intersection(possibility.requires, this.timeParts).length === possibility.requires.length;

                    if (!this.isFormatValid) {
                        // tslint:disable no-console
                        console.warn(`Invalid format! Falling back to ${this.unit}`);
                        break;
                    }
                }
            }
        }
    }
}
